import { useState, useRef, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/shared/PageHeader';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import { useAdminSearchUsers } from '@/hooks/useAdminUsers';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/Tooltip';
import {
    Save,
    Loader2,
    RotateCcw,
    Info,
    Calendar,
    Sliders,
    GitBranch,
    FlaskConical,
    Search,
    Play,
    Heart,
    MessageCircle,
    User,
    X,
    Camera,
    Video,
    Users,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// ─── Types ──────────────────────────────────────────────────────────────────

interface ConfigItem {
    key: string;
    value: unknown;
    updated_at: string | null;
}

interface FeedCoefficients {
    weight_impression_penalty?: number;
    weight_impressions_24h_penalty?: number;
    weight_engagement?: number;
    weight_preference_match?: number;
    weight_followers_engagement?: number;
    weight_recency_12h?: number;
    weight_new_creator?: number;
    weight_captures_vs_moments?: number;
    weight_view_duration?: number;
    max_impressions_before_heavy_penalty?: number;
    engagement_comment_weight?: number;
    feed_load_count_threshold?: number;
    feed_load_cap_minutes?: number;
    feed_load_cap_per_day?: number;
    feed_slot_new_pct?: number;
    feed_slot_discovery_pct?: number;
    /** % del límite 1ª pág reservado a rest con 0 impresiones (huecos no llenos → más rest general). */
    feed_slot_rest_unseen_pct?: number;
    feed_discovery_quality_threshold?: number;
    feed_max_items_per_author?: number;
    feed_shuffle_enabled?: boolean;
    weight_age_penalty?: number;
}

type SegmentKey = 'day_1' | 'day_2' | 'day_3' | 'day_4' | 'day_5' | 'day_6' | 'day_7' | 'default';

interface FeedAlgorithmByDay {
    day_1?: FeedCoefficients;
    day_2?: FeedCoefficients;
    day_3?: FeedCoefficients;
    day_4?: FeedCoefficients;
    day_5?: FeedCoefficients;
    day_6?: FeedCoefficients;
    day_7?: FeedCoefficients;
    default?: FeedCoefficients;
}

interface FeedItem {
    type: string;
    id: string;
    user_id: string;
    created_at: string;
    likes_count: number;
    comments_count: number;
    species?: string | null;
    profile_data?: { display_name?: string } | null;
}

const DEFAULT_COEFFICIENTS: Required<FeedCoefficients> = {
    weight_impression_penalty: 1.0,
    weight_impressions_24h_penalty: 100,
    weight_engagement: 1.0,
    weight_preference_match: 0.8,
    weight_followers_engagement: 0.5,
    weight_recency_12h: 0.5,
    weight_new_creator: 0.3,
    weight_captures_vs_moments: 1.0,
    weight_view_duration: 0.5,
    max_impressions_before_heavy_penalty: 10,
    engagement_comment_weight: 2,
    feed_load_count_threshold: 10,
    feed_load_cap_minutes: 15,
    feed_load_cap_per_day: 3,
    feed_slot_new_pct: 30,
    feed_slot_discovery_pct: 20,
    feed_slot_rest_unseen_pct: 12,
    feed_discovery_quality_threshold: 1.5,
    feed_max_items_per_author: 4,
    feed_shuffle_enabled: true,
    weight_age_penalty: 0.1,
};

const SEGMENTS: Array<{ key: SegmentKey; label: string; hint: string }> = [
    { key: 'day_1', label: 'Día 1', hint: 'Día de registro (hoy)' },
    { key: 'day_2', label: 'Día 2', hint: '1 día después del registro' },
    { key: 'day_3', label: 'Día 3', hint: '2 días después del registro' },
    { key: 'day_4', label: 'Día 4', hint: '3 días después del registro' },
    { key: 'day_5', label: 'Día 5', hint: '4 días después del registro' },
    { key: 'day_6', label: 'Día 6', hint: '5 días después del registro' },
    { key: 'day_7', label: 'Día 7', hint: '6 días después del registro' },
    { key: 'default', label: 'Día 8+ (por defecto)', hint: '7 o más días desde registro' },
];

const COEF_META: Array<{
    key: keyof FeedCoefficients;
    label: string;
    hint: string;
    tooltipContent: string;
    min: number;
    max: number;
    step: number;
}> = [
    {
        key: 'weight_impression_penalty',
        label: 'Penalización impresiones (histórico)',
        hint: 'Penaliza por todas las vistas previas',
        tooltipContent:
            'Penalización por impresión histórica (sin ventana temporal). A mayor valor, más bajan los ítems ya vistos. Enfoque híbrido: se usa en el ORDER BY de cada pool (new, discovery, rest), por lo que afecta a la SELECCIÓN, no solo al orden. Los vistos quedan fuera del top-N.',
        min: 0,
        max: 3,
        step: 0.1,
    },
    {
        key: 'weight_impressions_24h_penalty',
        label: 'Penalización impresiones 24h',
        hint: 'Vistas en últimas 24h van al final',
        tooltipContent:
            'Penalización fuerte por impresiones en las últimas 24 horas. Ventana deslizante (seen_at). Híbrido: se usa en el ORDER BY de cada pool, así que los ítems con impressions_24h > 0 quedan fuera del top-N de cada cuota (new ~90, discovery ~60, rest ~150). Mayor variabilidad tras refresh.',
        min: 0,
        max: 200,
        step: 5,
    },
    {
        key: 'weight_engagement',
        label: 'Peso engagement',
        hint: 'Likes + comentarios',
        tooltipContent:
            'Peso del engagement (likes + comentarios ponderados) en el scoring. LN(1+L+peso*C). Valores altos favorecen contenido con más interacción.',
        min: 0,
        max: 3,
        step: 0.1,
    },
    {
        key: 'weight_preference_match',
        label: 'Match preferencias',
        hint: 'Water type, fishing_modes, especies',
        tooltipContent:
            'Peso de la afinidad con las preferencias del usuario: tipo de agua, modalidades de pesca, especies que le gustan. 0 = ignorar preferencias. Fase 1: Ahora influye en el orden dentro de los pools (new_ordered, rest_ordered); antes feed_pos anulaba su efecto.',
        min: 0,
        max: 2,
        step: 0.1,
    },
    {
        key: 'weight_followers_engagement',
        label: 'Engagement seguidores',
        hint: 'Interacción de usuarios seguidos',
        tooltipContent:
            'Boost para contenido donde usuarios que sigue han interactuado (like/comentario). Favorece el contenido socialmente validado.',
        min: 0,
        max: 2,
        step: 0.1,
    },
    {
        key: 'weight_recency_12h',
        label: 'Recencia 12h',
        hint: 'Boost contenido <12h',
        tooltipContent:
            'Boost para contenido publicado en las últimas 12 horas. Fase 1: Ahora prioriza ítems recientes dentro de cada pool. Validado: variaciones cambian orden significativamente (66–85% de ítems se reordenan).',
        min: 0,
        max: 2,
        step: 0.1,
    },
    {
        key: 'weight_new_creator',
        label: 'Creador nuevo',
        hint: 'Boost autores <50 seguidores',
        tooltipContent:
            'Boost para autores con menos de 50 seguidores. Da visibilidad a creadores emergentes y evita monopolio de cuentas grandes.',
        min: 0,
        max: 1,
        step: 0.1,
    },
    {
        key: 'weight_captures_vs_moments',
        label: 'Capturas vs Momentos',
        hint: '1.0 = igual, >1 capturas',
        tooltipContent:
            'Balance entre capturas (fotos) y momentos (vídeos). 1.0 = trato igual. >1 favorece capturas, <1 favorece momentos. Validado: 0.5 → más momentos en top 20, 2.0 → más capturas. Usa la herramienta de sensibilidad para probar.',
        min: 0.5,
        max: 2,
        step: 0.1,
    },
    {
        key: 'weight_view_duration',
        label: 'Tiempo de vista',
        hint: 'Boost por tiempo visualizando',
        tooltipContent:
            'Boost para contenido que el usuario ha visualizado más tiempo. Usa LN(1+segundos). Híbrido: entra en el ORDER BY de new_ordered y rest_ordered; favorece contenido que genera interés y afecta la selección del top-N.',
        min: 0,
        max: 2,
        step: 0.1,
    },
    {
        key: 'max_impressions_before_heavy_penalty',
        label: 'Imp. antes penalización fuerte',
        hint: 'Umbral penalización severa',
        tooltipContent:
            'Número de impresiones a partir del cual la penalización por vista se vuelve más agresiva. Contenido muy visto baja aún más.',
        min: 5,
        max: 50,
        step: 1,
    },
    {
        key: 'engagement_comment_weight',
        label: 'Peso comentarios en engagement',
        hint: '2 = comentarios valen 2x likes',
        tooltipContent:
            'Dentro del engagement score: LN(1+L+peso*C). Un comentario cuenta como N likes. Alta sensibilidad: afecta el orden dentro de los pools (qué ítems entran en el top).',
        min: 0.5,
        max: 5,
        step: 0.5,
    },
    {
        key: 'feed_load_count_threshold',
        label: 'Umbral cargas onboarding',
        hint: 'Cargas antes de salir onboarding',
        tooltipContent:
            'Número de veces que el usuario debe abrir el feed antes de salir del modo onboarding. Durante onboarding ve contenido espectacular + reciente prioritariamente.',
        min: 1,
        max: 20,
        step: 1,
    },
    {
        key: 'feed_load_cap_minutes',
        label: 'Cap minutos entre cargas',
        hint: 'Anti-spam de conteo',
        tooltipContent:
            'Mínimo de minutos entre cargas para que cuente como nueva. Evita que refrescar muchas veces incremente el contador artificialmente.',
        min: 1,
        max: 60,
        step: 1,
    },
    {
        key: 'feed_load_cap_per_day',
        label: 'Cap cargas/día',
        hint: 'Máx. cargas contadas por día',
        tooltipContent:
            'Máximo de cargas que cuentan por día. Limita el avance del onboarding y evita abusos.',
        min: 1,
        max: 10,
        step: 1,
    },
    {
        key: 'feed_slot_new_pct',
        label: '% slots ventana nuevo',
        hint: 'Slots contenido <48h',
        tooltipContent:
            'Porcentaje de la primera página reservado para contenido reciente (<48h). Alta sensibilidad: cambia composición del feed. Round-robin con descubrimiento y resto.',
        min: 0,
        max: 100,
        step: 5,
    },
    {
        key: 'feed_slot_discovery_pct',
        label: '% slots descubrimiento',
        hint: 'Pref=0 + alta calidad',
        tooltipContent:
            'Porcentaje reservado para descubrimiento: contenido sin afinidad (pref_match=0) pero engagement alto. Alta sensibilidad. Evita filtros burbuja.',
        min: 0,
        max: 100,
        step: 5,
    },
    {
        key: 'feed_slot_rest_unseen_pct',
        label: '% rest 0 impresiones',
        hint: 'Cupo dentro del rest',
        tooltipContent:
            'Reserva dentro de la primera página para el pool “rest” solo con 0 impresiones (misma diversidad por autor). Si no hay tantos candidatos, el hueco se llena con el resto general (round-robin). Solo aplica con usuario logueado.',
        min: 0,
        max: 100,
        step: 5,
    },
    {
        key: 'feed_discovery_quality_threshold',
        label: 'Umbral calidad descubrimiento',
        hint: 'LN(1+L+coef*C) >= valor',
        tooltipContent:
            'Engagement mínimo para entrar en el pool de descubrimiento. Evita mostrar contenido de baja calidad sin afinidad.',
        min: 0.5,
        max: 5,
        step: 0.5,
    },
    {
        key: 'feed_max_items_per_author',
        label: 'Máx ítems por autor (1ª pág)',
        hint: 'Límite duro por autor',
        tooltipContent:
            'Ningún autor puede tener más de N ítems en cada pool. Regla dura de diversidad. Con pools grandes (sesión 300 ítems), 4 permite más contenido por creador sin dominar.',
        min: 1,
        max: 10,
        step: 1,
    },
    {
        key: 'weight_age_penalty',
        label: 'Penalización antigüedad',
        hint: 'Penaliza ítems viejos',
        tooltipContent:
            'Penalización por antigüedad. 0.1 ≈ 1 punto por cada 10 semanas. Reduce la presencia de contenido muy antiguo en la parte superior.',
        min: 0,
        max: 1,
        step: 0.05,
    },
];

// ─── Component ──────────────────────────────────────────────────────────────

interface SessionProfile {
    user_id: string;
    display_name: string | null;
    email: string | null;
}

export function FeedAlgorithmConfigPage() {
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useState<'coef' | 'algo' | 'sim' | 'session'>('coef');
    const [selectedSegment, setSelectedSegment] = useState<SegmentKey>('default');
    const [formBySegment, setFormBySegment] = useState<Partial<Record<SegmentKey, FeedCoefficients>>>({});

    // Herramienta de sensibilidad
    const [userSearch, setUserSearch] = useState('');
    const [selectedUser, setSelectedUser] = useState<{ user_id: string; display_name: string | null; email: string | null } | null>(null);
    const [sensResults, setSensResults] = useState<FeedItem[] | null>(null);
    const [sensError, setSensError] = useState<string | null>(null);
    const [sensLoading, setSensLoading] = useState(false);
    const [showUserDropdown, setShowUserDropdown] = useState(false);
    const userSearchRef = useRef<HTMLDivElement>(null);
    const [overrides, setOverrides] = useState<Partial<Record<keyof FeedCoefficients, number>>>({});

    // Tab Sesión: whitelist de usuarios beta
    const [sessionUserSearch, setSessionUserSearch] = useState('');
    const [sessionShowDropdown, setSessionShowDropdown] = useState(false);
    const [sessionIds, setSessionIds] = useState<string[]>([]);
    const sessionUserSearchRef = useRef<HTMLDivElement>(null);

    const { data: searchResults = [] } = useAdminSearchUsers(userSearch, { searchByEmail: true, limit: 15 });
    const { data: sessionSearchResults = [] } = useAdminSearchUsers(sessionUserSearch, { searchByEmail: true, limit: 15 });

    const { data: configItems = [], isLoading } = useQuery({
        queryKey: ['admin', 'app-config-all'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc<ConfigItem[]>('admin_get_all_config');
            if (error) throw error;
            return normalizeRpcResponse(data);
        },
    });

    const byDay = (configItems.find(c => c.key === 'feed_algorithm_by_day')?.value || {}) as FeedAlgorithmByDay;
    const legacyCoeff = (configItems.find(c => c.key === 'feed_algorithm_coefficients')?.value || {}) as FeedCoefficients;
    const feedSessionUserIdsConfig = configItems.find(c => c.key === 'feed_session_user_ids')?.value;

    useEffect(() => {
        const ids = Array.isArray(feedSessionUserIdsConfig)
            ? (feedSessionUserIdsConfig as unknown[]).map((id): string => String(id))
            : [];
        setSessionIds(ids);
    }, [feedSessionUserIdsConfig]);

    const getSegmentCoefficients = (seg: SegmentKey): FeedCoefficients => {
        const fromByDay = byDay[seg];
        const fallback = seg === 'default' ? legacyCoeff : DEFAULT_COEFFICIENTS;
        return { ...DEFAULT_COEFFICIENTS, ...fallback, ...fromByDay };
    };

    const merged = getSegmentCoefficients(selectedSegment);
    const formForSegment = formBySegment[selectedSegment] || {};
    const displayValues = { ...merged, ...formForSegment };

    const hasChanges =
        Object.keys(formBySegment).length > 0 &&
        Object.values(formBySegment).some(coef => Object.keys(coef).length > 0);

    const saveMutation = useMutation({
        mutationFn: async (data: FeedAlgorithmByDay) => {
            const { error } = await dbClient.rpc('admin_upsert_app_config', {
                p_key: 'feed_algorithm_by_day',
                p_value: data,
            });
            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'app-config-all'] });
            queryClient.invalidateQueries({ queryKey: ['admin', 'app-config'] });
            setFormBySegment({});
        },
    });

    const updateField = (key: keyof FeedCoefficients, value: number | boolean) => {
        setFormBySegment(prev => ({
            ...prev,
            [selectedSegment]: {
                ...(prev[selectedSegment] || {}),
                [key]: value,
            },
        }));
    };

    const handleSave = () => {
        const next: FeedAlgorithmByDay = { ...byDay };
        for (const seg of SEGMENTS.map(s => s.key)) {
            const edits = formBySegment[seg];
            if (edits && Object.keys(edits).length > 0) {
                const base = getSegmentCoefficients(seg);
                next[seg] = { ...base, ...edits };
            }
        }
        saveMutation.mutate(next);
    };

    const handleResetSegment = () => {
        setFormBySegment(prev => {
            const next = { ...prev };
            delete next[selectedSegment];
            return next;
        });
        saveMutation.mutate({
            ...byDay,
            [selectedSegment]: { ...DEFAULT_COEFFICIENTS },
        });
    };

    const handleResetAll = () => {
        const defaultByDay: FeedAlgorithmByDay = {};
        for (const seg of SEGMENTS.map(s => s.key)) {
            defaultByDay[seg] = DEFAULT_COEFFICIENTS;
        }
        setFormBySegment({});
        saveMutation.mutate(defaultByDay);
    };

    const SENS_OVERRIDE_KEYS: Array<keyof FeedCoefficients> = [
        'weight_captures_vs_moments',
        'weight_preference_match',
        'weight_recency_12h',
        'feed_slot_new_pct',
        'feed_slot_discovery_pct',
        'feed_slot_rest_unseen_pct',
        'weight_engagement',
    ];

    const runSensitivity = async () => {
        if (!selectedUser) {
            setSensError('Selecciona un usuario primero');
            return;
        }
        setSensError(null);
        setSensResults(null);
        setSensLoading(true);
        const overrideObj = Object.fromEntries(
            Object.entries(overrides).filter(([, v]) => v != null && !Number.isNaN(Number(v)))
        ) as Record<string, number>;
        try {
            const { data, error } = await dbClient.rpc<FeedItem[]>('admin_get_feed_sensitivity', {
                p_user_id: selectedUser.user_id,
                p_limit: 20,
                p_config_override: overrideObj,
                });
            if (error) throw error;
            const items = normalizeRpcResponse(data) as FeedItem[];
            setSensResults(items);
        } catch (e: any) {
            setSensError(e?.message || 'Error al obtener el feed');
        } finally {
            setSensLoading(false);
        }
    };

    const computeMetrics = (items: FeedItem[]) => {
        const n = items.length;
        const captures = items.filter(i => i.type === 'capture').length;
        const moments = n - captures;
        const totalLikes = items.reduce((s, i) => s + (Number(i.likes_count) || 0), 0);
        const totalComments = items.reduce((s, i) => s + (Number(i.comments_count) || 0), 0);
        const authors = new Set(items.map(i => i.user_id)).size;
        return {
            pctCaptures: n ? Math.round((captures / n) * 100) : 0,
            pctMoments: n ? Math.round((moments / n) * 100) : 0,
            engagement: totalLikes + totalComments,
            authors,
        };
    };

    const selectUser = (u: { user_id: string; display_name: string | null; email: string | null }) => {
        setSelectedUser(u);
        setUserSearch('');
        setShowUserDropdown(false);
    };

    const updateOverride = (key: keyof FeedCoefficients, value: number) => {
        if (Number.isNaN(value)) return;
        const base = merged[key] ?? DEFAULT_COEFFICIENTS[key];
        setOverrides(prev => {
            const next = { ...prev };
            if (value === base || (typeof base === 'number' && Math.abs(value - base) < 0.001)) {
                delete next[key];
            } else {
                next[key] = value;
            }
            return next;
        });
    };

    useEffect(() => {
        const h = (e: MouseEvent) => {
            if (userSearchRef.current && !userSearchRef.current.contains(e.target as Node)) {
                setShowUserDropdown(false);
            }
        };
        document.addEventListener('click', h);
        return () => document.removeEventListener('click', h);
    }, []);

    useEffect(() => {
        const h = (e: MouseEvent) => {
            if (sessionUserSearchRef.current && !sessionUserSearchRef.current.contains(e.target as Node)) {
                setSessionShowDropdown(false);
            }
        };
        document.addEventListener('click', h);
        return () => document.removeEventListener('click', h);
    }, []);

    const { data: sessionProfiles = [] } = useQuery({
        queryKey: ['admin', 'session-profiles', sessionIds.join(',')],
        queryFn: async () => {
            if (sessionIds.length === 0) return [];
            const { data, error } = await dbClient.rpc<SessionProfile[]>('admin_get_user_profiles_batch', {
                p_user_ids: sessionIds,
            });
            if (error) return [];
            return normalizeRpcResponse(data);
        },
        enabled: sessionIds.length > 0,
    });

    const saveSessionMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            const { error } = await dbClient.rpc('admin_upsert_app_config', {
                p_key: 'feed_session_user_ids',
                p_value: JSON.stringify(ids),
            });
            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'app-config-all'] });
        },
    });

    const addSessionUser = useCallback((userId: string) => {
        setSessionIds(prev => (prev.includes(userId) ? prev : [...prev, userId]));
        setSessionUserSearch('');
        setSessionShowDropdown(false);
    }, []);

    const removeSessionUser = useCallback((userId: string) => {
        setSessionIds(prev => prev.filter(id => id !== userId));
    }, []);

    const handleSaveSession = useCallback(() => {
        saveSessionMutation.mutate(sessionIds);
    }, [sessionIds, saveSessionMutation]);

    const cutoff48h = () => {
        const d = new Date();
        d.setHours(d.getHours() - 48);
        return d;
    };

    const getItemAge = (createdAt: string) => {
        const created = new Date(createdAt).getTime();
        const now = Date.now();
        return Math.floor((now - created) / (1000 * 60 * 60 * 24));
    };

    const getItemBadgeColor = (createdAt: string) => {
        const days = getItemAge(createdAt);
        if (days < 2) return 'bg-emerald-500/90 text-white';
        if (days < 7) return 'bg-amber-500/80 text-white';
        return 'bg-slate-500/70 text-white';
    };

    const isCapture = (type: string) => type === 'capture' || type === 'Capture';
    const getTypeChip = (type: string) =>
        isCapture(type) ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold bg-teal-500/20 text-teal-700 dark:text-teal-300 border border-teal-500/40 backdrop-blur-sm shadow-sm">
                <Camera size={12} />
                Captura
            </span>
        ) : (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold bg-violet-500/20 text-violet-700 dark:text-violet-300 border border-violet-500/40 backdrop-blur-sm shadow-sm">
                <Video size={12} />
                Momento
            </span>
        );
    const getTypeCardStyles = (type: string) =>
        isCapture(type)
            ? 'border-l-4 border-l-teal-500/60 ring-1 ring-teal-500/10'
            : 'border-l-4 border-l-violet-500/60 ring-1 ring-violet-500/10';

    return (
        <TooltipProvider delayDuration={200}>
            <div className="space-y-6">
                <PageHeader
                    title="Algoritmo del Feed"
                    description="Coeficientes por días desde registro. Día 1 = hoy; Día 8+ = usuarios establecidos."
                />

                {/* Tabs */}
                <div className="flex gap-1 p-1 bg-muted/50 rounded-lg w-fit">
                    <button
                        onClick={() => setActiveTab('coef')}
                        className={cn(
                            'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all',
                            activeTab === 'coef' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                        )}
                    >
                        <Sliders size={16} />
                        Coeficientes
                    </button>
                    <button
                        onClick={() => setActiveTab('algo')}
                        className={cn(
                            'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all',
                            activeTab === 'algo' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                        )}
                    >
                        <GitBranch size={16} />
                        Algoritmo
                    </button>
                    <button
                        onClick={() => setActiveTab('sim')}
                        className={cn(
                            'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all',
                            activeTab === 'sim' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                        )}
                    >
                        <FlaskConical size={16} />
                        Sensibilidad
                    </button>
                    <button
                        onClick={() => setActiveTab('session')}
                        className={cn(
                            'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all',
                            activeTab === 'session' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                        )}
                    >
                        <Users size={16} />
                        Sesión
                    </button>
                </div>

                {/* Tab: Coeficientes */}
                {activeTab === 'coef' && (
                    <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
                        {isLoading ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                            </div>
                        ) : (
                            <div className="space-y-6">
                                <div>
                                    <div className="flex items-center gap-2 mb-3">
                                        <Calendar className="h-4 w-4 text-muted-foreground" />
                                        <span className="text-sm font-medium">Segmento</span>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {SEGMENTS.map(({ key, label, hint }) => (
                                            <Tooltip key={key}>
                                                <TooltipTrigger asChild>
                                                    <button
                                                        type="button"
                                                        onClick={() => setSelectedSegment(key)}
                                                        className={cn(
                                                            'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                                                            selectedSegment === key ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
                                                        )}
                                                    >
                                                        {label}
                                                    </button>
                                                </TooltipTrigger>
                                                <TooltipContent className="max-w-[280px]">{hint}</TooltipContent>
                                            </Tooltip>
                                        ))}
                                    </div>
                                </div>

                                <div className="pt-2 border-t border-border">
                                    <p className="text-xs text-muted-foreground mb-4">
                                        Editando coeficientes para: <strong>{SEGMENTS.find(s => s.key === selectedSegment)?.label}</strong>
                                    </p>
                                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                        {COEF_META.map(({ key, label, hint, tooltipContent, min, max, step }) => (
                                            <div key={key} className="space-y-2">
                                                <div className="flex items-center gap-2">
                                                    <label htmlFor={`${selectedSegment}-${key}`} className="text-sm font-medium">
                                                        {label}
                                                    </label>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <span className="cursor-help">
                                                                <Info className="h-3.5 w-3.5 text-muted-foreground" />
                                                            </span>
                                                        </TooltipTrigger>
                                                        <TooltipContent className="max-w-[320px]">
                                                            <p className="font-medium mb-1">{hint}</p>
                                                            <p className="text-muted-foreground text-xs">{tooltipContent}</p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </div>
                                                <input
                                                    id={`${selectedSegment}-${key}`}
                                                    type="number"
                                                    min={min}
                                                    max={max}
                                                    step={step}
                                                    value={typeof displayValues[key] === 'number' ? displayValues[key] : ''}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                        const v = parseFloat(e.target.value);
                                                        if (!isNaN(v)) updateField(key, v);
                                                    }}
                                                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm font-mono shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                                />
                                            </div>
                                        ))}
                                    </div>

                                    <div className="mt-4 flex items-center gap-3">
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <button
                                                    type="button"
                                                    role="switch"
                                                    aria-checked={!!displayValues.feed_shuffle_enabled}
                                                    onClick={() => updateField('feed_shuffle_enabled', !displayValues.feed_shuffle_enabled)}
                                                    className={cn(
                                                        'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                                                        displayValues.feed_shuffle_enabled ? 'bg-primary' : 'bg-input'
                                                    )}
                                                >
                                                    <span
                                                        className={cn(
                                                            'pointer-events-none inline-block h-5 w-5 rounded-full bg-background shadow-lg ring-0 transition-transform',
                                                            displayValues.feed_shuffle_enabled ? 'translate-x-5' : 'translate-x-0'
                                                        )}
                                                    />
                                                </button>
                                            </TooltipTrigger>
                                            <TooltipContent className="max-w-[280px]">
                                                Varía el orden del feed cada día para el mismo usuario. Evita que el feed sea estático entre sesiones.
                                            </TooltipContent>
                                        </Tooltip>
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium">Shuffle diario</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex flex-wrap gap-3 border-t border-border pt-4">
                                    <button
                                        type="button"
                                        onClick={handleSave}
                                        disabled={saveMutation.isPending || !hasChanges}
                                        className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow transition-colors hover:bg-primary/90 disabled:opacity-50 disabled:pointer-events-none"
                                    >
                                        {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                                        <span>Guardar cambios</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleResetSegment}
                                        disabled={saveMutation.isPending}
                                        className="inline-flex items-center justify-center gap-2 rounded-md border border-input bg-background px-4 py-2 text-sm font-medium shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:pointer-events-none"
                                    >
                                        <RotateCcw className="h-4 w-4" />
                                        <span>Restaurar este segmento</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleResetAll}
                                        disabled={saveMutation.isPending}
                                        className="inline-flex items-center justify-center gap-2 rounded-md border border-input bg-background px-4 py-2 text-sm font-medium shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:pointer-events-none"
                                    >
                                        <RotateCcw className="h-4 w-4" />
                                        <span>Restaurar todos</span>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Tab: Algoritmo */}
                {activeTab === 'algo' && (
                    <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
                        <h3 className="text-xl font-semibold mb-2">Comportamiento del algoritmo del feed</h3>
                        <p className="text-sm text-muted-foreground mb-6">
                            Flujo unificado por <code className="text-xs">feed_algorithm_by_day</code>. Enfoque híbrido: las impresiones afectan tanto la selección como el orden en cada pool (new, discovery, rest).
                        </p>

                        <div className="space-y-6">
                            <section>
                                <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">1. Entrada y contexto</h4>
                                <div className="flex flex-wrap items-center gap-3">
                                    <div className="rounded-xl border-2 border-primary bg-primary/10 px-5 py-3 font-medium shadow-sm">
                                        Usuario abre feed
                                    </div>
                                    <span className="text-muted-foreground">→</span>
                                    <div className="rounded-lg border border-border bg-muted/30 px-4 py-2 text-sm">
                                        Coeficientes por <strong>día desde registro</strong> (day_1..day_7, default)
                                    </div>
                                    <span className="text-muted-foreground">→</span>
                                    <div className="rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/20 px-4 py-2 text-sm">
                                        ¿Primera página? (cursor=null)
                                    </div>
                                </div>
                                <p className="text-xs text-muted-foreground mt-2">Sí → cuotas + round-robin. No → paginación cursor.</p>
                            </section>

                            <section>
                                <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">2. Highlight y cuotas (1ª página)</h4>
                                <div className="space-y-3">
                                    <div className="rounded-lg border border-border bg-muted/20 px-4 py-2 text-sm">
                                        <strong>Slot 0 (opcional):</strong> Si hay highlight (capture o moment recién publicada), va siempre primero.
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium mb-2">Pools por cuota (1ª página)</p>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                            <div className="rounded-xl border-2 border-amber-400/60 bg-amber-500/15 p-4">
                                                <div className="font-semibold text-amber-700 dark:text-amber-400">Ventana nuevo</div>
                                                <div className="text-2xl font-bold mt-1">{displayValues.feed_slot_new_pct ?? 30}%</div>
                                                <p className="text-xs text-muted-foreground mt-1">Contenido &lt;48h</p>
                                            </div>
                                            <div className="rounded-xl border-2 border-violet-400/60 bg-violet-500/15 p-4">
                                                <div className="font-semibold text-violet-700 dark:text-violet-400">Descubrimiento</div>
                                                <div className="text-2xl font-bold mt-1">{displayValues.feed_slot_discovery_pct ?? 20}%</div>
                                                <p className="text-xs text-muted-foreground mt-1">pref_match=0, engagement ≥ {displayValues.feed_discovery_quality_threshold ?? 1.5}</p>
                                            </div>
                                            <div className="rounded-xl border-2 border-teal-400/60 bg-teal-500/15 p-4">
                                                <div className="font-semibold text-teal-700 dark:text-teal-400">Rest 0 imp.</div>
                                                <div className="text-2xl font-bold mt-1">{displayValues.feed_slot_rest_unseen_pct ?? 12}%</div>
                                                <p className="text-xs text-muted-foreground mt-1">Solo sin impresiones; si faltan candidatos, entra más rest general</p>
                                            </div>
                                            <div className="rounded-xl border-2 border-slate-400/60 bg-slate-500/15 p-4">
                                                <div className="font-semibold text-slate-700 dark:text-slate-400">Resto general</div>
                                                <div className="text-2xl font-bold mt-1">
                                                    {Math.max(
                                                        0,
                                                        100 -
                                                            (Number(displayValues.feed_slot_new_pct) || 30) -
                                                            (Number(displayValues.feed_slot_discovery_pct) || 20) -
                                                            (Number(displayValues.feed_slot_rest_unseen_pct) || 12)
                                                    )}
                                                    %
                                                </div>
                                                <p className="text-xs text-muted-foreground mt-1">Ordenado por score (incluye ya vistos)</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="rounded-lg border border-dashed border-primary/50 bg-primary/5 p-3">
                                        <p className="text-sm font-medium">Intercalado round-robin</p>
                                        <p className="text-xs text-muted-foreground">Los ítems se mezclan: nuevo → descubrimiento → rest 0 imp. → rest general → ciclo. Si un pool se agota antes, el siguiente en orden ocupa el hueco.</p>
                                        <div className="mt-2 flex flex-wrap gap-1">
                                            {['N', 'D', 'U', 'R', 'N', 'D', 'U', 'R', 'N', 'D', 'R', 'R', 'N', 'D', 'U', 'R', 'N', 'R', 'D', 'R'].map((c, i) => (
                                                <span
                                                    key={i}
                                                    className={cn(
                                                        'w-6 h-6 flex items-center justify-center rounded text-[10px] font-mono font-bold',
                                                        c === 'N' && 'bg-amber-500/80 text-white',
                                                        c === 'D' && 'bg-violet-500/80 text-white',
                                                        c === 'U' && 'bg-teal-600/85 text-white',
                                                        c === 'R' && 'bg-slate-500/70 text-white'
                                                    )}
                                                    title={
                                                        c === 'N'
                                                            ? 'Nuevo'
                                                            : c === 'D'
                                                              ? 'Descubrimiento'
                                                              : c === 'U'
                                                                ? 'Rest 0 imp.'
                                                                : 'Resto general'
                                                    }
                                                >
                                                    {c}
                                                </span>
                                            ))}
                                        </div>
                                        <p className="text-[10px] text-muted-foreground mt-1">N=Nuevo · D=Descubrimiento · U=Rest 0 imp. · R=Resto general (ejemplo)</p>
                                    </div>
                                    <div className="rounded-lg border border-border bg-muted/20 px-4 py-2 text-sm">
                                        <strong>Diversidad:</strong> Máx {displayValues.feed_max_items_per_author ?? 4} ítems por autor en la 1ª página.
                                    </div>
                                </div>
                            </section>

                            <section>
                                <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">3. Señales de ordenación (híbrido: orden = selección)</h4>
                                <p className="text-xs text-muted-foreground mb-2">
                                    Cada pool ordena con los mismos criterios y toma el top-N. El orden determina la selección: los ítems con impresiones altas quedan fuera del cupo new/discovery y del top del rest (incl. cupo rest 0 imp.). Incluye: tier, preference, recency_12h, <strong>impression_penalty</strong>, view_duration, dislikes, engagement.
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {[
                                        { lbl: 'Tier (unseen, recent, interacted)', w: '—' },
                                        { lbl: 'captures_vs_moments', w: displayValues.weight_captures_vs_moments ?? 1 },
                                        { lbl: 'preference_match', w: displayValues.weight_preference_match ?? 0.8 },
                                        { lbl: 'recency_12h', w: displayValues.weight_recency_12h ?? 0.5 },
                                        { lbl: 'engagement LN(1+L+2C)', w: displayValues.weight_engagement ?? 1 },
                                        { lbl: 'impression_penalty', w: displayValues.weight_impression_penalty ?? 1 },
                                        { lbl: 'impressions_24h_penalty', w: displayValues.weight_impressions_24h_penalty ?? 100 },
                                        { lbl: 'view_duration', w: displayValues.weight_view_duration ?? 0.5 },
                                        { lbl: 'followers_engagement', w: displayValues.weight_followers_engagement ?? 0.5 },
                                        { lbl: 'age_penalty', w: displayValues.weight_age_penalty ?? 0.1 },
                                        { lbl: 'new_creator (&lt;50 seg)', w: displayValues.weight_new_creator ?? 0.3 },
                                        { lbl: 'shuffle diario', w: displayValues.feed_shuffle_enabled ? 'ON' : 'OFF' },
                                    ].map(({ lbl, w }) => (
                                        <span
                                            key={lbl}
                                            className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-1.5 text-xs"
                                        >
                                            <span className="font-medium">{lbl}</span>
                                            <span className="text-muted-foreground">({w})</span>
                                        </span>
                                    ))}
                                </div>
                            </section>

                            <section>
                                <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">4. Salida y paginación</h4>
                                <div className="rounded-lg border border-border bg-muted/20 p-4 text-sm">
                                    <p><strong>Primera página:</strong> Highlight (si hay) + ítems por cuotas y señales (round-robin N/D/U/R).</p>
                                    <p className="mt-1"><strong>Páginas 2+ (modo clásico):</strong> Cursor. Sin cuotas; orden por señales secundarias.</p>
                                </div>
                                <div className="rounded-lg border border-blue-500/40 bg-blue-500/5 p-4 text-sm mt-4">
                                    <p className="font-medium text-blue-700 dark:text-blue-400">Modo sesión (beta)</p>
                                    <p className="text-xs text-muted-foreground mt-1">Cuando está activo (whitelist o flag global): el algoritmo se aplica a <strong>300 ítems</strong> de una vez, se guardan en Redis y se sirven en <strong>15 bloques de 20</strong>. Scroll = leer de Redis; pull-to-refresh invalida y recalcula.</p>
                                </div>
                                <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-4 text-sm mt-4">
                                    <p className="font-medium text-emerald-700 dark:text-emerald-400">Enfoque híbrido (Mar 2026)</p>
                                    <ul className="text-xs text-muted-foreground mt-1 space-y-1 list-disc list-inside">
                                        <li><strong>Impresiones en pools:</strong> impression_penalty, view_duration y dislikes en el ORDER BY de new, discovery y rest. La selección (top-N) depende del orden; los vistos quedan fuera.</li>
                                        <li><strong>Alta sensibilidad:</strong> feed_slot_new_pct, feed_slot_discovery_pct, feed_slot_rest_unseen_pct, feed_max_items_per_author, weight_impressions_24h_penalty</li>
                                        <li><strong>Valoración en pools:</strong> engagement_comment_weight, weight_preference_match, weight_recency_12h, weight_captures_vs_moments</li>
                                    </ul>
                                </div>
                            </section>
                        </div>
                    </div>
                )}

                {/* Tab: Herramienta de sensibilidad */}
                {activeTab === 'sim' && (
                    <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
                        <h3 className="text-lg font-semibold mb-4">Herramienta de sensibilidad</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                            Prueba cómo afectan los parámetros al feed para un usuario. Los overrides son temporales (no guardan en BD). Métricas: % capturas/momentos, engagement, diversidad de autores.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 mb-6">
                            <div className="relative flex-1 flex items-center gap-1" ref={userSearchRef}>
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                                <input
                                    type="text"
                                    placeholder="Buscar usuario por nombre o email (min. 2 caracteres)..."
                                    value={selectedUser ? (selectedUser.display_name || selectedUser.email || selectedUser.user_id) : userSearch}
                                    onChange={e => {
                                        const v = e.target.value;
                                        setUserSearch(v);
                                        if (v) setSelectedUser(null);
                                        setShowUserDropdown(!!v);
                                    }}
                                    onFocus={() => setShowUserDropdown(true)}
                                    className="w-full pl-9 pr-9 py-2 rounded-lg border border-border bg-background text-sm"
                                />
                                {selectedUser && (
                                    <button
                                        type="button"
                                        onClick={() => { setSelectedUser(null); setUserSearch(''); setSensResults(null); }}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                                        title="Limpiar"
                                    >
                                        <X size={14} />
                                    </button>
                                )}
                                {showUserDropdown && userSearch.trim().length >= 2 && searchResults.length > 0 && !selectedUser && (
                                    <div className="absolute top-full left-0 right-0 mt-1 rounded-lg border border-border bg-popover shadow-lg z-10 max-h-60 overflow-y-auto">
                                        {searchResults.map(u => (
                                            <button
                                                key={u.user_id}
                                                type="button"
                                                onClick={() => selectUser({ user_id: u.user_id, display_name: u.display_name ?? null, email: u.email ?? null })}
                                                className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-muted transition-colors"
                                            >
                                                <User size={14} className="text-muted-foreground shrink-0" />
                                                <div className="min-w-0">
                                                    <p className="text-sm font-medium truncate">{u.display_name || 'Sin nombre'}</p>
                                                    <p className="text-xs text-muted-foreground truncate">{u.email || u.user_id}</p>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={runSensitivity}
                                disabled={!selectedUser || sensLoading}
                                className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow hover:bg-primary/90 disabled:opacity-50 disabled:pointer-events-none"
                            >
                                {sensLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                                <span>{Object.keys(overrides).length ? 'Ejecutar con overrides' : 'Ejecutar (config actual)'}</span>
                            </button>
                        </div>

                        <div className="mb-6 p-4 rounded-lg border border-border bg-muted/20">
                            <p className="text-sm font-medium mb-3">Overrides para probar (no guardan en BD)</p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                                {SENS_OVERRIDE_KEYS.map(key => {
                                    const meta = COEF_META.find(m => m.key === key);
                                    const val = overrides[key] ?? merged[key] ?? DEFAULT_COEFFICIENTS[key];
                                    return meta ? (
                                        <div key={key} className="space-y-1">
                                            <label className="text-xs text-muted-foreground">{meta.label}</label>
                                            <input
                                                type="number"
                                                min={meta.min}
                                                max={meta.max}
                                                step={meta.step}
                                                value={typeof val === 'number' ? val : ''}
                                                onChange={e => updateOverride(key, parseFloat(e.target.value) || 0)}
                                                className="w-full h-8 rounded border border-input bg-background px-2 text-sm font-mono"
                                            />
                                        </div>
                                    ) : null;
                                })}
                            </div>
                            {Object.keys(overrides).length > 0 && (
                                <button
                                    type="button"
                                    onClick={() => setOverrides({})}
                                    className="mt-2 text-xs text-muted-foreground hover:text-foreground"
                                >
                                    Limpiar overrides
                                </button>
                            )}
                        </div>

                        {sensError && (
                            <div className="rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive mb-4">
                                {sensError}
                            </div>
                        )}

                        {sensResults && (
                            <div>
                                {(() => {
                                    const m = computeMetrics(sensResults);
                                    return (
                                        <div className="flex flex-wrap items-center gap-4 mb-3 text-sm">
                                            <span className="font-medium">{sensResults.length} ítems</span>
                                            <span className="text-muted-foreground">% capturas: <strong className="text-foreground">{m.pctCaptures}%</strong></span>
                                            <span className="text-muted-foreground">% momentos: <strong className="text-foreground">{m.pctMoments}%</strong></span>
                                            <span className="text-muted-foreground">Engagement (L+C): <strong className="text-foreground">{m.engagement}</strong></span>
                                            <span className="text-muted-foreground">Autores: <strong className="text-foreground">{m.authors}</strong></span>
                                            <span className="text-xs text-muted-foreground">Verde=&lt;2d · Ámbar=&lt;7d</span>
                                            <span className="flex items-center gap-2 text-xs">
                                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-teal-500/20 text-teal-700 dark:text-teal-300 border border-teal-500/40"><Camera size={10} /> Captura</span>
                                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-700 dark:text-violet-300 border border-violet-500/40"><Video size={10} /> Momento</span>
                                            </span>
                                        </div>
                                    );
                                })()}
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                                    {sensResults.map((item, idx) => (
                                        <div
                                            key={`${item.type}-${item.id}`}
                                            className={cn(
                                                'rounded-lg border p-3 space-y-2 transition-colors',
                                                getTypeCardStyles(item.type),
                                                getItemAge(item.created_at) < 2
                                                    ? 'border-emerald-500/50 bg-emerald-500/5'
                                                    : getItemAge(item.created_at) < 7
                                                      ? 'border-amber-500/40 bg-amber-500/5'
                                                      : 'border-border bg-muted/20'
                                            )}
                                        >
                                            <div className="flex items-center justify-between gap-2">
                                                {getTypeChip(item.type)}
                                                <div className="flex items-center gap-1.5 shrink-0">
                                                    <span className="text-[10px] font-mono text-muted-foreground">#{idx + 1}</span>
                                                    <span className={cn('text-[10px] px-1.5 py-0.5 rounded', getItemBadgeColor(item.created_at))}>
                                                        {getItemAge(item.created_at)}d
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-1.5 text-xs">
                                                <Heart size={10} className="text-muted-foreground" />
                                                <span>{Number(item.likes_count) || 0}</span>
                                                <MessageCircle size={10} className="text-muted-foreground ml-1" />
                                                <span>{Number(item.comments_count) || 0}</span>
                                            </div>
                                            <p className="text-xs font-medium truncate" title={item.species || ''}>
                                                {item.species || (item.type === 'moment' ? 'Moment' : 'Capture')}
                                            </p>
                                            <p className="text-[10px] text-muted-foreground truncate">
                                                {(item.profile_data as any)?.display_name || item.user_id?.slice(0, 8)}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Tab: Sesión (whitelist usuarios beta) */}
                {activeTab === 'session' && (
                    <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
                        <h3 className="text-lg font-semibold mb-2">Sesiones precomputadas (beta)</h3>
                        <p className="text-sm text-muted-foreground mb-6">
                            Usuarios que usan el feed con sesiones en Redis (algoritmo aplicado a 300 ítems, servidos en bloques de 20). Si la lista está vacía, se usa el flag global <code className="text-xs">feed_session_enabled</code>.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 mb-6">
                            <div className="relative flex-1" ref={sessionUserSearchRef}>
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                                <input
                                    type="text"
                                    placeholder="Buscar usuario por nombre o email (min. 2 caracteres)..."
                                    value={sessionUserSearch}
                                    onChange={e => {
                                        setSessionUserSearch(e.target.value);
                                        setSessionShowDropdown(!!e.target.value);
                                    }}
                                    onFocus={() => setSessionShowDropdown(true)}
                                    className="w-full pl-9 pr-9 py-2 rounded-lg border border-border bg-background text-sm"
                                />
                                {sessionShowDropdown && sessionUserSearch.trim().length >= 2 && sessionSearchResults.length > 0 && (
                                    <div className="absolute top-full left-0 right-0 mt-1 rounded-lg border border-border bg-popover shadow-lg z-10 max-h-60 overflow-y-auto">
                                        {sessionSearchResults
                                            .filter(u => !sessionIds.includes(u.user_id))
                                            .map(u => (
                                                <button
                                                    key={u.user_id}
                                                    type="button"
                                                    onClick={() => addSessionUser(u.user_id)}
                                                    className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-muted transition-colors"
                                                >
                                                    <User size={14} className="text-muted-foreground shrink-0" />
                                                    <div className="min-w-0">
                                                        <p className="text-sm font-medium truncate">{u.display_name || 'Sin nombre'}</p>
                                                        <p className="text-xs text-muted-foreground truncate">{u.email || u.user_id}</p>
                                                    </div>
                                                </button>
                                            ))}
                                        {sessionSearchResults.filter(u => !sessionIds.includes(u.user_id)).length === 0 && sessionSearchResults.length > 0 && (
                                            <p className="px-4 py-3 text-sm text-muted-foreground">Todos los resultados ya están en la whitelist</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="space-y-2 mb-6">
                            <p className="text-sm font-medium">
                                Usuarios en whitelist ({sessionIds.length})
                            </p>
                            {sessionIds.length === 0 ? (
                                <p className="text-sm text-muted-foreground py-4 rounded-lg border border-dashed border-border">
                                    Ningún usuario. Añade usuarios para que usen sesiones de feed.
                                </p>
                            ) : (
                                <div className="flex flex-wrap gap-2">
                                    {sessionIds.map(uid => {
                                        const profile = sessionProfiles.find((p: SessionProfile) => p.user_id === uid);
                                        return (
                                            <span
                                                key={uid}
                                                className="inline-flex items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm"
                                            >
                                                <User size={14} className="text-muted-foreground shrink-0" />
                                                <span className="truncate max-w-[200px]">
                                                    {profile?.display_name || profile?.email || uid.slice(0, 8) + '…'}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => removeSessionUser(uid)}
                                                    className="p-0.5 rounded hover:bg-destructive/20 text-muted-foreground hover:text-destructive transition-colors"
                                                    title="Quitar"
                                                >
                                                    <X size={14} />
                                                </button>
                                            </span>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        <button
                            type="button"
                            onClick={handleSaveSession}
                            disabled={saveSessionMutation.isPending}
                            className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow transition-colors hover:bg-primary/90 disabled:opacity-50 disabled:pointer-events-none"
                        >
                            {saveSessionMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                            <span>Guardar whitelist</span>
                        </button>
                    </div>
                )}
            </div>
        </TooltipProvider>
    );
}
